// ABOUTME: SPEC-82 Rev 3 (bug 2) — when a pre-tap guest request counts as the tap, and when the request may follow the welcome.
// ABOUTME: Pure: which actions release, and the waiting machine (idle -> waiting -> done) that the hook drives from wire signals.

/**
 * The guest requests that start a model turn (owner 2026-10-06: "any pre-tap interaction that makes the agent speak counts as the
 * tap"). Reachable before the tap: the collection menu's Vesper/Vélos entry and the Views pill (enter_collection / return_to_reel /
 * chapter_selected). The rest are listed so the rule does not depend on what happens to be on screen pre-tap today. NOT here, on
 * purpose: the bag / wishlist / privacy panels' housekeeping (removed from cart, increment/decrement/remove_cart_line, unpinned,
 * removed from wishlist, the panel open/close notices) never make the agent speak, and `stage_restore` is the app's own reconnect
 * echo, not the guest.
 */
const RELEASING_ACTIONS = new Set([
  'enter_collection', 'return_to_reel', 'chapter_selected', 'variant_selected', 'viewed', 'added to cart', 'added to wishlist',
]);

export const releases = (action) => RELEASING_ACTIONS.has(action);

/**
 * Review 2026-10-06 (MEDIUM): requests held behind the welcome are flushed together, and each starts a model turn — two screen moves
 * sent within milliseconds would have the second cut into the first. Screen moves share ONE slot (only the guest's latest move is
 * sent); cart / wishlist adds are real actions, each kept, in order.
 */
const SCREEN_MOVES = new Set(['enter_collection', 'return_to_reel', 'chapter_selected', 'variant_selected', 'viewed']);

/** @returns {string|null} the queue slot the action replaces within, or null when every one is kept */
export const queueSlot = (action) => (SCREEN_MOVES.has(action) ? 'screen' : null);
/** A screen move needs the agent to happen at all (no_enter_gate landing: it starts the conversation; cart/wishlist edits do not). */
export const isScreenMove = (action) => SCREEN_MOVES.has(action);

export const PHASE = Object.freeze({ IDLE: 'idle', WAITING: 'waiting', DONE: 'done' });
export const EVENT = Object.freeze({
  RELEASE: 'release', SPEAKING: 'speaking', SILENT: 'silent', BOUNDARY: 'boundary', CEILING: 'ceiling',
});

export const INITIAL = Object.freeze({ phase: PHASE.IDLE, spoke: false, boundary: false, speaking: false });

/**
 * The welcome is over when the agent has spoken since the release, a turn boundary has followed that speech, and the local playout
 * has drained. All three, because native audio reports turnComplete ~1 s before its last chunk plays (App.js sendInteraction note),
 * and the intro_finished injection itself provokes an `interrupted` boundary before the welcome's first word.
 *
 * @param {{phase: string, spoke: boolean, boundary: boolean, speaking: boolean}} state
 * @param {string} event one of EVENT
 * @returns {{phase: string, spoke: boolean, boundary: boolean, speaking: boolean}} the next state; DONE is terminal
 */
export function nextRelease(state, event) {
  if (state.phase === PHASE.DONE) return state;
  if (event === EVENT.RELEASE) return state.phase === PHASE.IDLE ? { ...INITIAL, phase: PHASE.WAITING } : state;
  if (state.phase !== PHASE.WAITING) return state;
  if (event === EVENT.CEILING) return { ...state, phase: PHASE.DONE };
  if (event === EVENT.SPEAKING) return { ...state, spoke: true, speaking: true };
  if (event === EVENT.BOUNDARY) return state.spoke ? settle({ ...state, boundary: true }) : state;
  if (event === EVENT.SILENT) return settle({ ...state, speaking: false });
  return state;
}

const settle = (s) => (s.spoke && s.boundary && !s.speaking ? { ...s, phase: PHASE.DONE } : s);
